import {
  OFFICIAL_RQT_PLAYABLE_CARDS,
  OFFICIAL_RQT_SAMPLES,
  compileGameDefinition,
  gameDefinitionToRoqeetJson,
  getOfficialRqtSample,
  isRoqeetGameDocument,
  newSyncMeta,
  roqeetJsonToGameDefinition,
  validateRoqeetGameDocument,
  type GameCard,
  type GameDefinition,
  type RoqeetGameDocument,
  type SyncMeta,
} from "@roqeet/core";

type AnyRecord = Record<string, unknown>;

export type StoredGameCardNormalizationResult =
  | { status: "ok"; card: GameCard; repaired: boolean; warnings: string[] }
  | { status: "invalid"; reason: string; rawId?: string; warnings: string[] };

const FALLBACK_TITLE = "Untitled Game";
const UNSAFE_OBJECT_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const officialSamples = [...OFFICIAL_RQT_SAMPLES, ...OFFICIAL_RQT_PLAYABLE_CARDS];

export function normalizeStoredGameCard(raw: unknown): StoredGameCardNormalizationResult {
  const warnings: string[] = [];
  try {
    return normalizeStoredGameCardInner(raw, warnings);
  } catch (error) {
    return {
      status: "invalid",
      reason: `Stored GameCard normalization failed: ${errorMessage(error)}`,
      ...(isRecord(raw) && nonEmptyString(raw["id"]) ? { rawId: nonEmptyString(raw["id"]) } : {}),
      warnings,
    };
  }
}

function normalizeStoredGameCardInner(
  raw: unknown,
  warnings: string[],
): StoredGameCardNormalizationResult {
  if (!isRecord(raw)) {
    return { status: "invalid", reason: "Stored GameCard row is not an object.", warnings };
  }

  const id = nonEmptyString(raw["id"]);
  if (!id) {
    return { status: "invalid", reason: "Stored GameCard row is missing id.", warnings };
  }

  let repaired = false;
  const sync = normalizeSync(raw["sync"], warnings);
  if (sync !== raw["sync"]) repaired = true;

  const documentResult = normalizeDocument(raw["document"], warnings);
  if (documentResult.repaired) repaired = true;

  const definitionResult = normalizeDefinition(raw["definition"], documentResult.document, id, warnings);
  if (!definitionResult.definition) {
    return { status: "invalid", reason: definitionResult.reason, rawId: id, warnings };
  }
  if (definitionResult.repaired) repaired = true;

  let document = documentResult.document;
  if (!document) {
    document = gameDefinitionToRoqeetJson(definitionResult.definition);
    warnings.push("Missing or invalid document was regenerated from definition.");
    repaired = true;
  }

  const extensionsResult = normalizeExtensions(raw["extensions"], warnings);
  if (extensionsResult.repaired) repaired = true;

  const officialResult = backfillOfficialSampleMetadata(extensionsResult.extensions, warnings);
  if (officialResult.repaired) repaired = true;

  const card: GameCard = {
    id: id as GameCard["id"],
    sync,
    definition: definitionResult.definition,
    document,
    enabled: typeof raw["enabled"] === "boolean" ? raw["enabled"] : true,
    ...(isRecord(raw["share"]) ? { share: raw["share"] as GameCard["share"] } : {}),
    ...(typeof raw["rating"] === "string" ? { rating: raw["rating"] as GameCard["rating"] } : {}),
    ...(Object.keys(officialResult.extensions).length ? { extensions: officialResult.extensions } : {}),
  };
  if (raw["enabled"] !== card.enabled) {
    warnings.push("Missing enabled flag was backfilled.");
    repaired = true;
  }

  try {
    compileGameDefinition(card.definition);
  } catch (error) {
    return {
      status: "invalid",
      reason: `Stored GameCard definition cannot compile: ${errorMessage(error)}`,
      rawId: id,
      warnings,
    };
  }

  return { status: "ok", card, repaired, warnings };
}

export function storedGameCardSortKey(card: GameCard): string {
  return card.definition.title.trim() || card.definition.id || card.id;
}

function normalizeDefinition(
  rawDefinition: unknown,
  document: RoqeetGameDocument | undefined,
  cardId: string,
  warnings: string[],
): { definition?: GameDefinition; repaired: boolean; reason: string } {
  let repaired = false;
  let documentDefinition: GameDefinition | undefined;
  if (document) {
    try {
      documentDefinition = roqeetJsonToGameDefinition(document);
    } catch (error) {
      warnings.push(`Stored document could not rebuild definition: ${errorMessage(error)}`);
      repaired = true;
    }
  }

  if (!isRecord(rawDefinition)) {
    if (!documentDefinition) {
      return {
        repaired,
        reason: "Stored GameCard row is missing a recoverable definition.",
      };
    }
    warnings.push("Missing definition was regenerated from document.");
    return {
      definition: { ...documentDefinition, id: cardId },
      repaired: true,
      reason: "",
    };
  }

  const source = rawDefinition as Partial<GameDefinition>;
  const title =
    nonEmptyString(source.title) ??
    nonEmptyString(documentDefinition?.title) ??
    FALLBACK_TITLE;
  if (title !== source.title) {
    warnings.push("Missing definition title was backfilled.");
    repaired = true;
  }

  const rawInitialState = source.initialState;
  const hasValidInitialState = isRecord(rawInitialState);
  const initialState = hasValidInitialState
    ? cloneRecord(rawInitialState)
    : documentDefinition?.initialState
      ? cloneRecord(documentDefinition.initialState)
      : {};
  if (!hasValidInitialState) {
    warnings.push("Missing or malformed initialState was backfilled.");
    repaired = true;
  }

  const instructions = typeof source.instructions === "string"
    ? source.instructions
    : documentDefinition?.instructions ?? "";
  if (instructions !== source.instructions) {
    warnings.push("Missing instructions were backfilled.");
    repaired = true;
  }

  const id = nonEmptyString(source.id) ?? cardId;
  if (id !== source.id || id !== cardId) {
    warnings.push("Definition id was aligned with card id.");
    repaired = true;
  }

  return {
    definition: {
      ...(documentDefinition ?? {}),
      ...source,
      id: cardId,
      title,
      initialState,
      instructions,
    },
    repaired,
    reason: "",
  };
}

function normalizeDocument(
  rawDocument: unknown,
  warnings: string[],
): { document?: RoqeetGameDocument; repaired: boolean } {
  if (rawDocument === undefined) {
    return { repaired: false };
  }
  if (!isRoqeetGameDocument(rawDocument)) {
    warnings.push("Malformed document was ignored.");
    return { repaired: true };
  }
  const errors = validateRoqeetGameDocument(rawDocument).filter((issue) => issue.severity === "error");
  if (errors.length > 0) {
    warnings.push(`Malformed document was ignored: ${errors[0]?.message ?? "validation failed"}`);
    return { repaired: true };
  }
  return { document: rawDocument, repaired: false };
}

function normalizeSync(rawSync: unknown, warnings: string[]): SyncMeta {
  if (isRecord(rawSync)) {
    const rev = typeof rawSync["rev"] === "number" ? rawSync["rev"] : undefined;
    const createdAt = typeof rawSync["createdAt"] === "number" ? rawSync["createdAt"] : undefined;
    const updatedAt = typeof rawSync["updatedAt"] === "number" ? rawSync["updatedAt"] : undefined;
    const deletedAt = rawSync["deletedAt"];
    if (
      rev !== undefined &&
      createdAt !== undefined &&
      updatedAt !== undefined &&
      (deletedAt === null || typeof deletedAt === "number" || deletedAt === undefined)
    ) {
      if (deletedAt !== undefined) return rawSync as unknown as SyncMeta;
      warnings.push("Missing sync tombstone marker was backfilled.");
      return {
        ...rawSync,
        deletedAt: null,
      } as SyncMeta;
    }
  }
  warnings.push("Missing or malformed sync metadata was backfilled.");
  return newSyncMeta();
}

function normalizeExtensions(
  rawExtensions: unknown,
  warnings: string[],
): { extensions: AnyRecord; repaired: boolean } {
  if (rawExtensions === undefined) return { extensions: {}, repaired: false };
  if (isRecord(rawExtensions)) return { extensions: { ...rawExtensions }, repaired: false };
  warnings.push("Malformed extensions were ignored.");
  return { extensions: {}, repaired: true };
}

function backfillOfficialSampleMetadata(
  extensions: AnyRecord,
  warnings: string[],
): { extensions: AnyRecord; repaired: boolean } {
  if (extensions["sourceKind"] === "user-copy" || extensions["sourceKind"] === "based-on-official-sample") {
    return { extensions, repaired: false };
  }
  if (typeof extensions["basedOnOfficialRqtSampleId"] === "string") {
    return { extensions, repaired: false };
  }

  const explicitSampleId = nonEmptyString(extensions["officialRqtSampleId"]);
  const sourceRqt = typeof extensions["sourceRqt"] === "string" ? extensions["sourceRqt"] : undefined;
  const canInferFromExactSource =
    sourceRqt &&
    (extensions["sourceRqtStatus"] === undefined || extensions["sourceKind"] === "official-sample");
  const exactSourceSample = canInferFromExactSource
    ? officialSamples.find((sample) => sample.source === sourceRqt)
    : undefined;
  const sample = explicitSampleId ? getOfficialRqtSample(explicitSampleId) : exactSourceSample;
  if (!sample) return { extensions, repaired: false };

  let repaired = false;
  const next = { ...extensions };
  const sourceKind = next["sourceKind"];
  if (sourceKind === undefined && (explicitSampleId || exactSourceSample)) {
    next["sourceKind"] = "official-sample";
    warnings.push("Legacy official sample sourceKind was backfilled.");
    repaired = true;
  }

  if (next["officialRqtSampleId"] === undefined) {
    next["officialRqtSampleId"] = sample.id;
    warnings.push("Legacy official sample id was backfilled from exact source.");
    repaired = true;
  }
  repaired = setIfMissing(next, "officialRqtSampleFilename", sample.filename, warnings, "Official sample filename was backfilled.") || repaired;
  repaired = setIfMissing(next, "officialRqtSampleSourcePath", sample.sourcePath, warnings, "Official sample source path was backfilled.") || repaired;

  if (next["sourceRqtStatus"] === undefined && sourceRqt === sample.source) {
    next["sourceRqtStatus"] = "current";
    warnings.push("Official sample source status was backfilled as current from exact source.");
    repaired = true;
  }

  return { extensions: next, repaired };
}

function setIfMissing(
  target: AnyRecord,
  key: string,
  value: string,
  warnings: string[],
  warning: string,
): boolean {
  if (target[key] !== undefined) return false;
  target[key] = value;
  warnings.push(warning);
  return true;
}

function cloneRecord(value: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    if (UNSAFE_OBJECT_KEYS.has(key)) continue;
    out[key] = cloneUnknown(item);
  }
  return out;
}

function cloneUnknown(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(cloneUnknown);
  if (isRecord(value)) return cloneRecord(value);
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  return undefined;
}

function isRecord(value: unknown): value is AnyRecord {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
